import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { PostulacionApiDto } from '../contracts/backend-api.contracts';
import { POSTULACIONES_MOCK } from '../mocks/postulaciones.mock';
import { Concurso } from '../models/inicio.models';
import { InvitacionPostulacion } from '../models/invitaciones.models';
import { ReporteConcursoMonitoreo, ResumenEstadoPostulacion } from '../models/monitoreo.models';
import {
  DocumentoAdjuntoPostulacion,
  EstadoRegistroParticipante,
  FormularioPostulacionData,
  InstitucionFormulario,
  Postulacion,
  PresupuestoInstitucionFormulario,
  PresupuestoItemFormulario,
  RegistroParticipanteFormulario,
} from '../models/postulaciones.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PostulacionesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/postulaciones`;
  private readonly draftOverrides = new Map<string, Partial<Postulacion>>();
  private readonly postulacionesMock$ = new BehaviorSubject<Postulacion[]>(POSTULACIONES_MOCK);

  constructor(private readonly http: HttpClient) {}

  getMisPostulaciones(): Observable<Postulacion[]> {
    if (!environment.useMockApi) {
      return this.http
        .get<PostulacionApiDto[]>(`${this.baseUrl}/mis-postulaciones`)
        .pipe(map((items) => items.map((dto) => this.aplicarDraft(this.fromApiDto(dto)))));
    }

    const payload = this.postulacionesMock$.value.map((item) => this.toApiDto(item));
    return of(payload.map((dto) => this.aplicarDraft(this.fromApiDto(dto))));
  }

  getPostulacionById(idPostulacion: string): Observable<Postulacion | null> {
    if (!idPostulacion) return of(null);
    return this.getMisPostulaciones().pipe(map((items) => items.find((item) => item.idPostulacion === idPostulacion) ?? null));
  }

  getReporteConcurso(concurso: string): Observable<ReporteConcursoMonitoreo | null> {
    if (!concurso.trim()) return of(null);

    return this.getMisPostulaciones().pipe(
      map((items) => {
        const normalizado = this.normalizarTexto(concurso);
        const relacionadas = items.filter((item) => {
          const nombre = this.normalizarTexto(item.concurso);
          return nombre === normalizado || nombre.includes(normalizado) || normalizado.includes(nombre);
        });

        if (relacionadas.length === 0) return null;

        const total = relacionadas.length;
        const resumenEstados = this.construirResumenEstados(relacionadas, total);
        const ultimaActualizacion = relacionadas
          .map((item) => item.ultimaActualizacion ?? item.fechaTermino ?? item.fechaInicio)
          .filter((valor): valor is string => Boolean(valor))
          .sort((a, b) => b.localeCompare(a))[0] ?? null;

        return {
          concurso,
          subdireccion: relacionadas[0]?.subdireccion ?? '',
          totalPostulaciones: total,
          avancePromedio: Math.round(relacionadas.reduce((acc, item) => acc + (item.avancePorcentaje ?? 0), 0) / total),
          totalEnviadas: relacionadas.filter((item) => item.estadoPostulacion === 'enviada' || item.enviadaPorPostulante).length,
          totalEnPreparacion: relacionadas.filter((item) => item.estadoPostulacion === 'en_preparacion').length,
          totalObservadas: relacionadas.filter((item) => item.estadoPostulacion === 'observada').length,
          totalConPatrocinio: relacionadas.filter((item) => item.estadoPatrocinio === 'patrocinado' || item.solicitudPatrocinio).length,
          ultimaActualizacion,
          estados: resumenEstados,
        };
      })
    );
  }

  guardarDetallePostulacion(
    idPostulacion: string,
    cambios: Partial<Pick<Postulacion, 'nombreProyecto' | 'departamentoFacultad' | 'formularioData'>>
  ): Observable<Postulacion | null> {
    if (!idPostulacion) return of(null);

    if (!environment.useMockApi) {
      return this.http
        .put<PostulacionApiDto>(`${this.baseUrl}/${idPostulacion}`, {
          nombre_proyecto: cambios.nombreProyecto,
          departamento_facultad: cambios.departamentoFacultad,
          formulario_data: this.toFormularioApiDto(cambios.formularioData),
        })
        .pipe(map((dto) => this.aplicarDraft(this.fromApiDto(dto))));
    }

    const previo = this.draftOverrides.get(idPostulacion) ?? {};
    this.draftOverrides.set(idPostulacion, {
      ...previo,
      ...cambios,
      formularioData: this.combinarFormularioData(this.extraerFormularioData(previo), cambios.formularioData),
      ultimaActualizacion: new Date().toISOString(),
    });
    return this.getPostulacionById(idPostulacion);
  }

  iniciarPostulacionDesdeConcurso(concurso: Concurso): Observable<Postulacion | null> {
    if (!concurso) return of(null);

    if (!environment.useMockApi) {
      return this.http
        .post<PostulacionApiDto>(this.baseUrl, { concurso_id: concurso.id })
        .pipe(map((dto) => this.aplicarDraft(this.fromApiDto(dto))));
    }

    const existente = this.postulacionesMock$.value.find(
      (item) => item.concurso === concurso.nombre && item.rolesPermitidos.includes('postulante') && !item.enviadaPorPostulante
    );

    if (existente) {
      return of(this.aplicarDraft(existente));
    }

    const nuevaPostulacion = this.crearPostulacionDesdeConcurso(concurso);
    this.postulacionesMock$.next([nuevaPostulacion, ...this.postulacionesMock$.value]);
    return of(nuevaPostulacion);
  }

  enviarPostulacion(
    idPostulacion: string,
    cambios?: Partial<Pick<Postulacion, 'nombreProyecto' | 'departamentoFacultad' | 'formularioData'>>
  ): Observable<Postulacion | null> {
    if (!idPostulacion) return of(null);

    if (!environment.useMockApi) {
      return this.http
        .post<PostulacionApiDto>(`${this.baseUrl}/${idPostulacion}/enviar`, {
          nombre_proyecto: cambios?.nombreProyecto,
          departamento_facultad: cambios?.departamentoFacultad,
          formulario_data: this.toFormularioApiDto(cambios?.formularioData),
        })
        .pipe(map((dto) => this.aplicarDraft(this.fromApiDto(dto))));
    }

    const actualizadas = this.postulacionesMock$.value.map((item) => {
      if (item.idPostulacion !== idPostulacion) return item;

      const combinado = this.aplicarDraft(item);
      const formularioData = this.combinarFormularioData(combinado.formularioData, cambios?.formularioData);
      const actualizado: Postulacion = {
        ...combinado,
        nombreProyecto: cambios?.nombreProyecto?.trim() || combinado.nombreProyecto,
        departamentoFacultad: cambios?.departamentoFacultad?.trim() || combinado.departamentoFacultad,
        formularioData,
        estadoPostulacion: 'enviada',
        enviada: true,
        enviadaPorPostulante: true,
        editablePorAyudante: false,
        puedeEditar: false,
        puedeEnviar: false,
        ultimaActualizacion: new Date().toISOString(),
      };

      return {
        ...actualizado,
        avancePorcentaje: Math.max(100, this.calcularAvanceFormulario(actualizado)),
      };
    });

    this.draftOverrides.delete(idPostulacion);
    this.postulacionesMock$.next(actualizadas);
    return of(actualizadas.find((item) => item.idPostulacion === idPostulacion) ?? null);
  }

  calcularAvancePostulacion(postulacion: Postulacion): number {
    return this.calcularAvanceFormulario(postulacion);
  }

  actualizarParticipantePorInvitacion(
    invitacion: InvitacionPostulacion,
    estadoRespuesta: 'aceptada' | 'rechazada'
  ): void {
    if (!environment.useMockApi) return;

    const estadoParticipante: EstadoRegistroParticipante = estadoRespuesta === 'aceptada' ? 'aceptado' : 'rechazado';

    const actualizadas = this.postulacionesMock$.value.map((item) =>
      item.idPostulacion === invitacion.idPostulacion
        ? this.actualizarPostulacionConInvitacion(item, invitacion, estadoParticipante)
        : item
    );

    this.postulacionesMock$.next(actualizadas);

    const draft = this.draftOverrides.get(invitacion.idPostulacion);
    if (!draft) return;

    const baseActualizada = actualizadas.find((item) => item.idPostulacion === invitacion.idPostulacion);
    if (!baseActualizada) return;

    const formularioData = this.actualizarFormularioConInvitacion(
      this.combinarFormularioData(baseActualizada.formularioData, this.extraerFormularioData(draft)),
      invitacion,
      estadoParticipante
    );

    this.draftOverrides.set(invitacion.idPostulacion, {
      ...draft,
      formularioData,
      ultimaActualizacion: new Date().toISOString(),
    });
  }

  private fromApiDto(dto: PostulacionApiDto): Postulacion {
    const rolAsociado =
      dto.rol_asociado ??
      (dto.roles_permitidos.includes('ayudante_postulante') ? 'ayudante_postulante' : 'postulante');
    const enviada = dto.enviada ?? dto.enviada_por_postulante;
    const ayudanteActivo = dto.ayudante_activo ?? dto.roles_permitidos.includes('ayudante_postulante');
    const estadoEditable = this.estadoPermiteEdicion(dto.estado_postulacion);
    const formularioData = this.crearFormularioBase(
      {
        nombreProyecto: dto.nombre_proyecto,
        departamentoFacultad: dto.departamento_facultad,
        concurso: dto.concurso,
      },
      dto.formulario_data
    );
    const model: Postulacion = {
      idPostulacion: dto.id_postulacion,
      enviada,
      rolAsociado,
      ayudanteActivo,
      puedeEditar:
        dto.puede_editar ??
        ((rolAsociado === 'ayudante_postulante' && ayudanteActivo && estadoEditable) ||
          (rolAsociado === 'postulante' && estadoEditable)),
      puedeEnviar: dto.puede_enviar ?? (rolAsociado === 'postulante' && estadoEditable),
      firmaPatrocinioRealizada: dto.firma_patrocinio_realizada ?? (dto.estado_patrocinio === 'patrocinado'),
      ultimaActualizacion: dto.ultima_actualizacion,
      concurso: dto.concurso,
      nombreProyecto: dto.nombre_proyecto,
      subdireccion: dto.subdireccion,
      departamentoFacultad: dto.departamento_facultad,
      estadoConcurso: dto.estado_concurso,
      folio: dto.folio,
      trackingUrl: dto.tracking_url,
      estadoPostulacion: dto.estado_postulacion,
      anio: dto.anio,
      fechaInicio: dto.fecha_inicio,
      fechaTermino: dto.fecha_termino,
      rolesPermitidos: dto.roles_permitidos,
      enviadaPorPostulante: dto.enviada_por_postulante,
      editablePorAyudante: dto.editable_por_ayudante,
      estadoInvitacion: dto.estado_invitacion,
      estadoPatrocinio: dto.estado_patrocinio,
      solicitudPatrocinio: dto.solicitud_patrocinio,
      avancePorcentaje: dto.avance_porcentaje ?? 0,
      asignadaAnalista: dto.asignada_analista ?? false,
      formularioData,
    };

    return {
      ...model,
      avancePorcentaje: dto.avance_porcentaje ?? this.calcularAvanceFormulario(model),
    };
  }

  private toApiDto(model: Postulacion): PostulacionApiDto {
    return {
      id_postulacion: model.idPostulacion,
      enviada: model.enviada ?? model.enviadaPorPostulante,
      rol_asociado: model.rolAsociado,
      ayudante_activo: model.ayudanteActivo,
      puede_editar: model.puedeEditar,
      puede_enviar: model.puedeEnviar,
      firma_patrocinio_realizada: model.firmaPatrocinioRealizada,
      ultima_actualizacion: model.ultimaActualizacion,
      concurso: model.concurso,
      nombre_proyecto: model.nombreProyecto,
      subdireccion: model.subdireccion,
      departamento_facultad: model.departamentoFacultad,
      estado_concurso: model.estadoConcurso,
      folio: model.folio,
      tracking_url: model.trackingUrl,
      estado_postulacion: model.estadoPostulacion,
      anio: model.anio,
      fecha_inicio: model.fechaInicio,
      fecha_termino: model.fechaTermino,
      roles_permitidos: model.rolesPermitidos,
      enviada_por_postulante: model.enviadaPorPostulante,
      editable_por_ayudante: model.editablePorAyudante,
      estado_invitacion: model.estadoInvitacion,
      estado_patrocinio: model.estadoPatrocinio,
      solicitud_patrocinio: model.solicitudPatrocinio,
      avance_porcentaje: model.avancePorcentaje ?? 0,
      asignada_analista: model.asignadaAnalista ?? false,
      formulario_data: this.toFormularioApiDto(model.formularioData),
    };
  }

  private aplicarDraft(model: Postulacion): Postulacion {
    const draft = this.draftOverrides.get(model.idPostulacion);
    if (!draft) return model;

    const combinado = {
      ...model,
      ...draft,
      formularioData: this.combinarFormularioData(model.formularioData, this.extraerFormularioData(draft)),
    };

    return {
      ...combinado,
      avancePorcentaje: this.calcularAvanceFormulario(combinado),
    };
  }

  private actualizarPostulacionConInvitacion(
    postulacion: Postulacion,
    invitacion: InvitacionPostulacion,
    estado: EstadoRegistroParticipante
  ): Postulacion {
    const formularioBase =
      postulacion.formularioData ??
      this.crearFormularioBase({
        nombreProyecto: postulacion.nombreProyecto,
        departamentoFacultad: postulacion.departamentoFacultad,
        concurso: postulacion.concurso,
      });

    const formularioData = this.actualizarFormularioConInvitacion(formularioBase, invitacion, estado);
    const actualizada = {
      ...postulacion,
      formularioData,
      ultimaActualizacion: new Date().toISOString(),
    };

    return {
      ...actualizada,
      avancePorcentaje: this.calcularAvanceFormulario(actualizada),
    };
  }

  private actualizarFormularioConInvitacion(
    formularioData: FormularioPostulacionData,
    invitacion: InvitacionPostulacion,
    estado: EstadoRegistroParticipante
  ): FormularioPostulacionData {
    const registroActualizado = this.crearRegistroParticipanteDesdeInvitacion(formularioData, invitacion, estado);
    const registrosPrevios = formularioData.registroParticipantes ?? [];
    const correoInvitacion = this.normalizarCorreo(registroActualizado.email);

    const existeRegistro = registrosPrevios.some((registro) =>
      this.esMismoRegistroInvitacion(registro, registroActualizado, correoInvitacion)
    );

    return {
      ...formularioData,
      registroParticipantes: existeRegistro
        ? registrosPrevios.map((registro) =>
            this.esMismoRegistroInvitacion(registro, registroActualizado, correoInvitacion)
              ? {
                  ...registro,
                  ...registroActualizado,
                }
              : registro
          )
        : [registroActualizado, ...registrosPrevios],
    };
  }

  private crearRegistroParticipanteDesdeInvitacion(
    formularioData: FormularioPostulacionData,
    invitacion: InvitacionPostulacion,
    estado: EstadoRegistroParticipante
  ): RegistroParticipanteFormulario {
    const rol: RegistroParticipanteFormulario['rol'] = invitacion.rolDestino === 'recomendador' ? 'recomendador' : 'colaborador';

    return {
      idInvitacion: invitacion.id,
      rol,
      nombres: invitacion.nombresDestino?.trim() || invitacion.emailDestino?.trim() || invitacion.tipoParticipacion || 'Participante',
      apellidos: invitacion.apellidosDestino?.trim() ?? '',
      rut: invitacion.rutDestino?.trim() ?? '',
      email: invitacion.emailDestino?.trim() ?? '',
      institucionAsociada: this.resolverInstitucionParticipante(formularioData, invitacion),
      estado,
      fechaSolicitud: invitacion.fechaSolicitud ?? new Date().toISOString(),
    };
  }

  private esMismoRegistroInvitacion(
    registro: RegistroParticipanteFormulario,
    registroActualizado: RegistroParticipanteFormulario,
    correoInvitacion: string
  ): boolean {
    if (registro.idInvitacion && registroActualizado.idInvitacion) {
      return registro.idInvitacion === registroActualizado.idInvitacion;
    }

    return (
      registro.rol === registroActualizado.rol &&
      correoInvitacion.length > 0 &&
      this.normalizarCorreo(registro.email) === correoInvitacion
    );
  }

  private resolverInstitucionParticipante(
    formularioData: FormularioPostulacionData,
    invitacion: InvitacionPostulacion
  ): string {
    const institucionInvitacion = invitacion.institucionAsociada?.trim();
    if (institucionInvitacion) return institucionInvitacion;

    const principal = formularioData.instituciones.find((institucion) => institucion.rol === 'principal');
    return (
      principal?.nombreInstitucion.trim() ||
      formularioData.institucionPatrocinante.trim() ||
      formularioData.instituciones[0]?.nombreInstitucion.trim() ||
      ''
    );
  }

  private crearPostulacionDesdeConcurso(concurso: Concurso): Postulacion {
    const siguienteId = this.generarSiguienteIdPostulacion();
    const siguienteFolio = this.generarSiguienteFolio();
    const anio = Number.parseInt(concurso.fechaApertura.slice(0, 4), 10) || new Date().getFullYear();

    return {
      idPostulacion: siguienteId,
      enviada: false,
      rolAsociado: 'postulante',
      ayudanteActivo: true,
      puedeEditar: true,
      puedeEnviar: true,
      ultimaActualizacion: new Date().toISOString(),
      concurso: concurso.nombre,
      nombreProyecto: `Proyecto en preparacion - ${concurso.nombre}`,
      subdireccion: concurso.subdireccion,
      departamentoFacultad: 'Por definir',
      estadoConcurso: concurso.estado === 'abierto' ? 'abierto' : 'cerrado',
      folio: siguienteFolio,
      trackingUrl: `https://www.anid.cl/tracking/${siguienteFolio}`,
      estadoPostulacion: 'en_preparacion',
      anio,
      fechaInicio: concurso.fechaApertura,
      fechaTermino: concurso.fechaCierre,
      rolesPermitidos: ['postulante', 'ayudante_postulante'],
      enviadaPorPostulante: false,
      editablePorAyudante: true,
      estadoInvitacion: 'no_aplica',
      estadoPatrocinio: 'no_aplica',
      solicitudPatrocinio: false,
      avancePorcentaje: 0,
      asignadaAnalista: false,
      formularioData: this.crearFormularioBase({
        nombreProyecto: `Proyecto en preparacion - ${concurso.nombre}`,
        departamentoFacultad: 'Por definir',
        concurso: concurso.nombre,
      }),
    };
  }

  private crearFormularioBase(
    contexto: Pick<Postulacion, 'nombreProyecto' | 'departamentoFacultad' | 'concurso'>,
    formularioData?: PostulacionApiDto['formulario_data']
  ): FormularioPostulacionData {
    const esBeca = this.esConcursoBeca(contexto.concurso);
    const institucionPatrocinante = formularioData?.institucion_patrocinante ?? contexto.departamentoFacultad;
    const unidadInstitucional = formularioData?.unidad_institucional ?? contexto.departamentoFacultad;
    const instituciones = this.normalizarInstitucionesFormulario(formularioData, {
      institucionPatrocinante,
      unidadInstitucional,
      departamentoFacultad: contexto.departamentoFacultad,
    });

    return {
      nombreInstitucion: formularioData?.nombre_institucion ?? '',
      nombreProgramaEstudios: formularioData?.nombre_programa_estudios ?? '',
      regionProgramaEstudios: formularioData?.region_programa_estudios ?? '',
      especialidadWos: formularioData?.especialidad_wos ?? '',
      subAreaOcde: formularioData?.sub_area_ocde ?? '',
      sitioWebUniversidad: formularioData?.sitio_web_universidad ?? '',
      sitioWebPrograma: formularioData?.sitio_web_programa ?? '',
      fechaInicioEstudios: formularioData?.fecha_inicio_estudios ?? '',
      estadoProgramaEstudios: formularioData?.estado_programa_estudios ?? '',
      correoContacto: formularioData?.correo_contacto ?? '',
      telefonoContacto: formularioData?.telefono_contacto ?? '',
      resumenProyecto: formularioData?.resumen_proyecto ?? contexto.nombreProyecto,
      objetivoGeneral: formularioData?.objetivo_general ?? '',
      lineaInvestigacion: formularioData?.linea_investigacion ?? contexto.concurso,
      institucionPatrocinante,
      unidadInstitucional,
      recomendadorAsignado: formularioData?.recomendador_asignado ?? '',
      colaboradorAsignado: formularioData?.colaborador_asignado ?? '',
      patrocinioSolicitado: formularioData?.patrocinio_solicitado ?? '',
      patrocinadorAsignado: formularioData?.patrocinador_asignado ?? '',
      instituciones,
      registroParticipantes: (formularioData?.registro_participantes ?? []).map((registro) => ({
        idInvitacion: registro.id_invitacion,
        rol: registro.rol,
        nombres: registro.nombres,
        apellidos: registro.apellidos,
        rut: registro.rut,
        email: registro.email,
        institucionAsociada: registro.institucion_asociada ?? '',
        estado: registro.estado,
        fechaSolicitud: registro.fecha_solicitud,
      })),
      duracionPresupuestoAnios: this.resolverDuracionPresupuestoAnios(
        formularioData?.duracion_presupuesto_anios,
        formularioData?.presupuesto_por_institucion,
        formularioData?.presupuesto_items
      ),
      presupuestoPorInstitucion: this.mapearPresupuestoInstitucionesDesdeApi(formularioData?.presupuesto_por_institucion),
      presupuestoItems: this.mapearPresupuestoItemsDesdeApi(formularioData?.presupuesto_items),
      recursosHumanos: formularioData?.recursos_humanos ?? 0,
      equipamiento: formularioData?.equipamiento ?? 0,
      infraestructura: formularioData?.infraestructura ?? 0,
      operacionFuncionamiento: formularioData?.operacion_funcionamiento ?? 0,
      viajesViaticos: formularioData?.viajes_viaticos ?? 0,
      publicacionesDifusion: formularioData?.publicaciones_difusion ?? 0,
      serviciosExternos: formularioData?.servicios_externos ?? 0,
      softwareLicencias: formularioData?.software_licencias ?? 0,
      capacitacionFormacion: formularioData?.capacitacion_formacion ?? 0,
      materialesInsumos: formularioData?.materiales_insumos ?? 0,
      totalSolicitado:
        formularioData?.total_solicitado ??
        (esBeca ? 0 : 0),
      documentosAdjuntos: this.normalizarDocumentosAdjuntos(formularioData?.documentos_adjuntos),
    };
  }

  private toFormularioApiDto(formularioData?: Partial<FormularioPostulacionData>): PostulacionApiDto['formulario_data'] | undefined {
    if (!formularioData) return undefined;

    return {
      nombre_institucion: formularioData.nombreInstitucion,
      nombre_programa_estudios: formularioData.nombreProgramaEstudios,
      region_programa_estudios: formularioData.regionProgramaEstudios,
      especialidad_wos: formularioData.especialidadWos,
      sub_area_ocde: formularioData.subAreaOcde,
      sitio_web_universidad: formularioData.sitioWebUniversidad,
      sitio_web_programa: formularioData.sitioWebPrograma,
      fecha_inicio_estudios: formularioData.fechaInicioEstudios,
      estado_programa_estudios: formularioData.estadoProgramaEstudios,
      correo_contacto: formularioData.correoContacto,
      telefono_contacto: formularioData.telefonoContacto,
      resumen_proyecto: formularioData.resumenProyecto,
      objetivo_general: formularioData.objetivoGeneral,
      linea_investigacion: formularioData.lineaInvestigacion,
      institucion_patrocinante: formularioData.institucionPatrocinante,
      unidad_institucional: formularioData.unidadInstitucional,
      instituciones: formularioData.instituciones?.map((institucion) => ({
        nombre_institucion: institucion.nombreInstitucion,
        unidad_institucional: institucion.unidadInstitucional,
        departamento_facultad: institucion.departamentoFacultad,
        rol: institucion.rol,
      })),
      recomendador_asignado: formularioData.recomendadorAsignado,
      colaborador_asignado: formularioData.colaboradorAsignado,
      patrocinio_solicitado: formularioData.patrocinioSolicitado,
      patrocinador_asignado: formularioData.patrocinadorAsignado,
      registro_participantes: formularioData.registroParticipantes?.map((registro) => ({
        id_invitacion: registro.idInvitacion,
        rol: registro.rol,
        nombres: registro.nombres,
        apellidos: registro.apellidos,
        rut: registro.rut,
        email: registro.email,
        institucion_asociada: registro.institucionAsociada,
        estado: registro.estado,
        fecha_solicitud: registro.fechaSolicitud,
      })),
      duracion_presupuesto_anios: formularioData.duracionPresupuestoAnios,
      presupuesto_por_institucion: formularioData.presupuestoPorInstitucion?.map((presupuesto) => ({
        nombre_institucion: presupuesto.nombreInstitucion,
        unidad_institucional: presupuesto.unidadInstitucional,
        departamento_facultad: presupuesto.departamentoFacultad,
        rol_institucion: presupuesto.rolInstitucion,
        items: presupuesto.items.map((item) => ({
          item: item.item,
          subitems: item.subitems.map((subitem) => ({
            nombre: subitem.nombre,
            montos_por_anio: subitem.montosPorAnio.map((monto) => Number(monto) || 0),
          })),
        })),
      })),
      presupuesto_items: formularioData.presupuestoItems?.map((item) => ({
        item: item.item,
        subitems: item.subitems.map((subitem) => ({
          nombre: subitem.nombre,
          montos_por_anio: subitem.montosPorAnio.map((monto) => Number(monto) || 0),
        })),
      })),
      recursos_humanos: formularioData.recursosHumanos,
      equipamiento: formularioData.equipamiento,
      infraestructura: formularioData.infraestructura,
      operacion_funcionamiento: formularioData.operacionFuncionamiento,
      viajes_viaticos: formularioData.viajesViaticos,
      publicaciones_difusion: formularioData.publicacionesDifusion,
      servicios_externos: formularioData.serviciosExternos,
      software_licencias: formularioData.softwareLicencias,
      capacitacion_formacion: formularioData.capacitacionFormacion,
      materiales_insumos: formularioData.materialesInsumos,
      total_solicitado: formularioData.totalSolicitado,
      documentos_adjuntos: formularioData.documentosAdjuntos?.map((documento) => ({
        id: documento.id,
        tipo: documento.tipo,
        nombre_archivo: documento.nombreArchivo,
        extension: documento.extension,
        tamano_bytes: documento.tamanoBytes,
        obligatorio: documento.obligatorio,
        estado: documento.estado,
        fecha_carga: documento.fechaCarga,
      })),
    };
  }

  private extraerFormularioData(origen: Partial<Postulacion>): Partial<FormularioPostulacionData> {
    return origen.formularioData ?? {};
  }

  private combinarFormularioData(
    base?: Partial<FormularioPostulacionData>,
    cambios?: Partial<FormularioPostulacionData>
  ): FormularioPostulacionData {
    return {
      ...this.formularioDataVacio(),
      ...base,
      ...cambios,
    };
  }

  private formularioDataVacio(): FormularioPostulacionData {
    return {
      nombreInstitucion: '',
      nombreProgramaEstudios: '',
      regionProgramaEstudios: '',
      especialidadWos: '',
      subAreaOcde: '',
      sitioWebUniversidad: '',
      sitioWebPrograma: '',
      fechaInicioEstudios: '',
      estadoProgramaEstudios: '',
      correoContacto: '',
      telefonoContacto: '',
      resumenProyecto: '',
      objetivoGeneral: '',
      lineaInvestigacion: '',
      institucionPatrocinante: '',
      unidadInstitucional: '',
      recomendadorAsignado: '',
      colaboradorAsignado: '',
      patrocinioSolicitado: '',
      patrocinadorAsignado: '',
      instituciones: [],
      registroParticipantes: [],
      duracionPresupuestoAnios: 3,
      presupuestoPorInstitucion: [],
      presupuestoItems: [],
      recursosHumanos: 0,
      equipamiento: 0,
      infraestructura: 0,
      operacionFuncionamiento: 0,
      viajesViaticos: 0,
      publicacionesDifusion: 0,
      serviciosExternos: 0,
      softwareLicencias: 0,
      capacitacionFormacion: 0,
      materialesInsumos: 0,
      totalSolicitado: 0,
      documentosAdjuntos: [],
    };
  }

  private calcularAvanceFormulario(postulacion: Postulacion): number {
    const formulario = postulacion.formularioData;
    if (!formulario) return postulacion.avancePorcentaje ?? 0;

    const camposEvaluables = [
      formulario.nombreInstitucion,
      formulario.nombreProgramaEstudios,
      formulario.regionProgramaEstudios,
      formulario.especialidadWos,
      formulario.subAreaOcde,
      formulario.sitioWebUniversidad,
      formulario.fechaInicioEstudios,
      formulario.estadoProgramaEstudios,
      formulario.resumenProyecto,
      formulario.objetivoGeneral,
      formulario.lineaInvestigacion,
      formulario.institucionPatrocinante,
      formulario.unidadInstitucional,
      formulario.recomendadorAsignado,
      formulario.colaboradorAsignado,
      formulario.patrocinioSolicitado,
      formulario.patrocinadorAsignado,
      formulario.recursosHumanos > 0 ? 'ok' : '',
      formulario.equipamiento > 0 ? 'ok' : '',
      formulario.infraestructura > 0 ? 'ok' : '',
      formulario.operacionFuncionamiento > 0 ? 'ok' : '',
      formulario.viajesViaticos > 0 ? 'ok' : '',
      formulario.publicacionesDifusion > 0 ? 'ok' : '',
      formulario.serviciosExternos > 0 ? 'ok' : '',
      formulario.softwareLicencias > 0 ? 'ok' : '',
      formulario.capacitacionFormacion > 0 ? 'ok' : '',
      formulario.materialesInsumos > 0 ? 'ok' : '',
      formulario.totalSolicitado > 0 ? 'ok' : '',
      this.tieneDocumentoObligatorio(formulario.documentosAdjuntos, 'carta_compromiso') ? 'ok' : '',
      this.tieneDocumentoObligatorio(formulario.documentosAdjuntos, 'cv_postulante') ? 'ok' : '',
      this.tieneDocumentoObligatorio(formulario.documentosAdjuntos, 'presupuesto_financiero') ? 'ok' : '',
    ];

    const completos = camposEvaluables.filter((item) => String(item ?? '').trim().length > 0).length;
    return Math.round((completos / camposEvaluables.length) * 100);
  }

  private esConcursoBeca(concurso: string): boolean {
    const normalizado = concurso.toLowerCase();
    return normalizado.includes('beca') || normalizado.includes('capital humano');
  }

  private normalizarInstitucionesFormulario(
    formularioData: PostulacionApiDto['formulario_data'] | undefined,
    defaults: {
      institucionPatrocinante: string;
      unidadInstitucional: string;
      departamentoFacultad: string;
    }
  ): InstitucionFormulario[] {
    const instituciones = (formularioData?.instituciones ?? [])
      .map((institucion, index) => ({
        nombreInstitucion: this.normalizarValorInstitucion(institucion.nombre_institucion),
        unidadInstitucional: this.normalizarValorInstitucion(institucion.unidad_institucional),
        departamentoFacultad: this.normalizarValorInstitucion(institucion.departamento_facultad),
        rol: index === 0 ? 'principal' : institucion.rol,
      }))
      .filter((institucion) =>
        [institucion.nombreInstitucion, institucion.unidadInstitucional, institucion.departamentoFacultad].every((valor) => valor.length > 0)
      );

    if (instituciones.length > 0) return instituciones;

    const hayDatosBase = [defaults.institucionPatrocinante, defaults.unidadInstitucional, defaults.departamentoFacultad].every((valor) =>
      this.esValorInstitucionReal(valor)
    );

    return hayDatosBase
      ? [
          {
            nombreInstitucion: this.normalizarValorInstitucion(defaults.institucionPatrocinante),
            unidadInstitucional: this.normalizarValorInstitucion(defaults.unidadInstitucional),
            departamentoFacultad: this.normalizarValorInstitucion(defaults.departamentoFacultad),
            rol: 'principal',
          },
        ]
      : [];
  }

  private normalizarValorInstitucion(valor: string | undefined): string {
    const limpio = String(valor ?? '').trim();
    return this.esValorInstitucionReal(limpio) ? limpio : '';
  }

  private esValorInstitucionReal(valor: string | undefined): boolean {
    const normalizado = String(valor ?? '').trim().toLowerCase();
    return normalizado.length > 0 && normalizado !== 'por definir';
  }

  private mapearPresupuestoItemsDesdeApi(
    items: NonNullable<PostulacionApiDto['formulario_data']>['presupuesto_items']
  ): PresupuestoItemFormulario[] {
    return (items ?? []).map((item) => ({
      item: item.item,
      subitems: item.subitems.map((subitem) => ({
        nombre: subitem.nombre,
        montosPorAnio: (subitem.montos_por_anio ?? []).map((monto) => Number(monto) || 0),
      })),
    }));
  }

  private mapearPresupuestoInstitucionesDesdeApi(
    presupuestos: NonNullable<PostulacionApiDto['formulario_data']>['presupuesto_por_institucion']
  ): PresupuestoInstitucionFormulario[] {
    return (presupuestos ?? []).map((presupuesto) => ({
      nombreInstitucion: this.normalizarValorInstitucion(presupuesto.nombre_institucion),
      unidadInstitucional: this.normalizarValorInstitucion(presupuesto.unidad_institucional),
      departamentoFacultad: this.normalizarValorInstitucion(presupuesto.departamento_facultad),
      rolInstitucion: presupuesto.rol_institucion,
      items: this.mapearPresupuestoItemsDesdeApi(presupuesto.items),
    }));
  }

  private resolverDuracionPresupuestoAnios(
    duracion: number | undefined,
    presupuestoPorInstitucion: NonNullable<PostulacionApiDto['formulario_data']>['presupuesto_por_institucion'],
    presupuestoItems: NonNullable<PostulacionApiDto['formulario_data']>['presupuesto_items']
  ): number {
    const duracionBackend = Number(duracion);
    if (Number.isFinite(duracionBackend) && duracionBackend > 0) return Math.trunc(duracionBackend);

    const items = [
      ...(presupuestoItems ?? []),
      ...(presupuestoPorInstitucion ?? []).flatMap((presupuesto) => presupuesto.items),
    ];

    const duracionDesdeItems = items.reduce((maximo, item) => {
      const maximoSubitems = item.subitems.reduce((maximoSubitem, subitem) => {
        return Math.max(maximoSubitem, subitem.montos_por_anio?.length ?? 0);
      }, 0);

      return Math.max(maximo, maximoSubitems);
    }, 0);

    return duracionDesdeItems > 0 ? duracionDesdeItems : 3;
  }

  private estadoPermiteEdicion(estado: Postulacion['estadoPostulacion']): boolean {
    return estado === 'en_preparacion' || estado === 'observada';
  }

  private normalizarDocumentosAdjuntos(
    documentos?: NonNullable<PostulacionApiDto['formulario_data']>['documentos_adjuntos']
  ): DocumentoAdjuntoPostulacion[] {
    return (documentos ?? []).map((documento) => ({
      id: documento.id,
      tipo: documento.tipo,
      nombreArchivo: documento.nombre_archivo,
      extension: documento.extension,
      tamanoBytes: documento.tamano_bytes,
      obligatorio: documento.obligatorio,
      estado: documento.estado,
      fechaCarga: documento.fecha_carga,
    }));
  }

  private tieneDocumentoObligatorio(
    documentos: DocumentoAdjuntoPostulacion[] | undefined,
    tipo: DocumentoAdjuntoPostulacion['tipo']
  ): boolean {
    return (documentos ?? []).some((documento) => documento.tipo === tipo && documento.estado === 'cargado');
  }

  private generarSiguienteIdPostulacion(): string {
    const max = this.postulacionesMock$.value.reduce((acumulado, item) => {
      const numero = Number.parseInt(item.idPostulacion.replace('P-', ''), 10);
      return Number.isNaN(numero) ? acumulado : Math.max(acumulado, numero);
    }, 1000);

    return `P-${max + 1}`;
  }

  private generarSiguienteFolio(): string {
    const max = this.postulacionesMock$.value.reduce((acumulado, item) => {
      const numero = Number.parseInt(item.folio, 10);
      return Number.isNaN(numero) ? acumulado : Math.max(acumulado, numero);
    }, 500000);

    return String(max + 1);
  }

  private construirResumenEstados(items: Postulacion[], total: number): ResumenEstadoPostulacion[] {
    const estadosBase: Postulacion['estadoPostulacion'][] = [
      'en_preparacion',
      'observada',
      'enviada',
      'en_revision',
      'proximo',
      'adjudicado',
    ];

    return estadosBase
      .map((estado) => {
        const cantidad = items.filter((item) => item.estadoPostulacion === estado).length;
        return {
          estado,
          cantidad,
          porcentaje: total > 0 ? Math.round((cantidad / total) * 100) : 0,
        };
      })
      .filter((item) => item.cantidad > 0);
  }

  private normalizarTexto(valor: string): string {
    return valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }

  private normalizarCorreo(valor: string | undefined): string {
    return String(valor ?? '').trim().toLowerCase();
  }
}
